/**
 * admin/makua/ReservationDrawer.js — one reservation, everything staff do to it.
 *
 * Opened from the booking calendar, the reservations list, guests and the
 * schedule. Sections (V2.1, Page 5): Overview, Guests, Accommodation,
 * Bill & Payments, Add-ons, Notes, History. Every action goes to the
 * server, which re-checks rooms, seats and places and writes the history;
 * date changes are previewed (new nights, new bill) before they're saved.
 */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { reservationsAdminAPI, inventoryAPI } from "../../services/api";
import { usePermissions, hasPermission } from "../permissions";
import {
  money, fmtDate, fmtStamp, range, useLoad, Field, Modal,
  StatusBadge, Badge, SCREENING,
} from "./ops";
import { useUnsaved } from "./kit";

const ACTION_WORDS = {
  created: "Created", confirmed: "Confirmed", held_again: "Held again", cancelled: "Cancelled",
  payment_recorded: "Payment recorded", refund_recorded: "Refund recorded", room_moved: "Room moved",
  updated: "Edited", guest_updated: "Guest edited", screening_updated: "Screening changed",
  addon_updated: "Add-on changed", line_added: "Line added", line_removed: "Line removed",
  dates_changed: "Dates changed", guest_updated_by_seller: "Guest edited by the seller",
};
const SECTIONS = [["overview", "Overview"], ["guests", "Guests"], ["rooms", "Accommodation"], ["money", "Bill & Payments"], ["addons", "Add-ons"], ["notes", "Notes"], ["history", "History"]];

export default function ReservationDrawer({ id, onClose, onChanged }) {
  const { admin } = usePermissions();
  const canManage = hasPermission(admin, "reservations.manage");
  const canPay = hasPermission(admin, "payments.record");
  const { data, loading, error, reload } = useLoad(() => reservationsAdminAPI.get(id), [id]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState("overview");
  const [notesDirty, setNotesDirty] = useState(false);
  const guard = useUnsaved(notesDirty);
  useEffect(() => { setSection("overview"); }, [id]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.warning) toast.error(res.message, { duration: 7000 });
      else toast.success(res?.message || okMsg);
      (res?.data?.warnings || []).forEach((w) => toast(w, { icon: "⚠", duration: 9000 }));
      await reload(); onChanged?.();
      return true;
    } catch (err) { toast.error(err.message); return false; }
    finally { setBusy(false); }
  };

  const r = data?.reservation;
  const live = r && !["cancelled", "expired"].includes(r.status);
  const count = (k) => (k === "guests" ? r.guests.length : k === "addons" ? r.addons.length : k === "history" ? data.history.length : null);
  return (
    <>
      <div className="ops-drawer-scrim" onClick={guard(onClose)} />
      <aside className="ops-drawer" aria-label="Reservation">
        <div className="ops-drawer-head">
          <div>
            <h2>{r ? `${r.ref} · ${r.guests[0]?.first_name || ""} ${r.guests[0]?.last_name || ""}` : "Reservation"}</h2>
            {r && <p className="ops-muted" style={{ margin: "0.2rem 0 0" }}>{r.retreat_name} · {range(r.arrive_date, r.depart_date)} · {r.nights} nights</p>}
          </div>
          <button type="button" className="ops-close" aria-label="Close" onClick={guard(onClose)}>×</button>
        </div>

        <div className="ops-drawer-body">
          {loading && !r && <p className="ops-muted">Loading…</p>}
          {error && <p className="ops-error">{error}</p>}
          {r && (
            <>
              {/* Always in view: where it stands and what can be done */}
              <div className="ops-card ops-res-head">
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <StatusBadge status={r.status} />
                  {r.status === "pending_payment" && <span className="ops-muted">held until <b>{fmtStamp(r.hold_expires_at)}</b></span>}
                  <Badge>{r.source === "web" ? "Website" : r.source === "admin" ? "Staff" : "Seller"}</Badge>
                  <span className="ops-muted">balance <b>{money(r.balance)}</b> of {money(r.total)}</span>
                </div>
                {canManage && (
                  <div className="ops-actions">
                    {live && <button className="ops-btn ops-btn--sm" disabled={busy} onClick={() => setModal("dates")}>Change dates</button>}
                    {["pending_payment", "expired", "requested"].includes(r.status) && (
                      <button className="ops-btn ops-btn--sm" disabled={busy} onClick={() => setModal("hold")}>
                        {r.status === "requested" ? "Approve request" : r.status === "expired" ? "Hold again" : "Extend hold"}
                      </button>
                    )}
                    {["pending_payment", "expired"].includes(r.status) && (
                      <button className="ops-btn ops-btn--sm" disabled={busy}
                        onClick={() => window.confirm("Confirm without the payment that's due? This is recorded in the history.") && act(() => reservationsAdminAPI.confirm(r.id, { reason: "confirmed by staff" }), "Confirmed.")}>
                        Confirm now
                      </button>
                    )}
                    {r.status !== "cancelled" && (
                      <button className="ops-btn ops-btn--sm ops-btn--danger" disabled={busy} onClick={() => setModal("cancel")}>Cancel</button>
                    )}
                  </div>
                )}
              </div>

              <div className="ops-tabs ops-tabs--sticky" role="tablist" aria-label="Reservation sections">
                {SECTIONS.map(([k, l]) => {
                  const n = count(k);
                  return <button key={k} role="tab" aria-selected={section === k} className={`ops-tab ${section === k ? "is-on" : ""}`} onClick={() => setSection(k)}>{l}{n ? ` (${n})` : ""}</button>;
                })}
              </div>

              {section === "overview" && (
                <div className="ops-card">
                  <dl className="ops-kv">
                    <dt>Stay</dt><dd>{fmtDate(r.arrive_date)} to {fmtDate(r.depart_date)} · {r.nights} nights{r.nights_before || r.nights_after ? ` (${r.nights_before} before, ${r.nights_after} after the retreat)` : ""}</dd>
                    <dt>Retreat</dt><dd>{r.retreat_name} · {range(r.retreat_start, r.retreat_end)}</dd>
                    <dt>Guests</dt><dd>{r.guests.map((g) => `${g.first_name} ${g.last_name}`).join(", ")}</dd>
                    <dt>Room</dt><dd>{r.room_type_name || "—"}{r.stays.length ? ` · ${r.stays.map((s) => `${s.room_name}${s.bed_name ? ` · ${s.bed_name}` : ""}`).join(", ")}` : " · no room yet"}</dd>
                    <dt>Total</dt><dd><b>{money(r.total)}</b> · paid {money(r.paid)} · balance {money(r.balance)}</dd>
                    <dt>Due now</dt><dd>{money(r.due_now)} ({r.payment_plan === "deposit" ? "deposit" : r.payment_plan === "plan" ? "instalment plan" : "full payment"}, {r.payment_method})</dd>
                    <dt>Balance due</dt><dd>{r.balance_due_date ? fmtDate(r.balance_due_date) : "—"}</dd>
                    <dt>Booked</dt><dd>{fmtStamp(r.created_at)}{r.confirmed_at ? ` · confirmed ${fmtStamp(r.confirmed_at)}` : ""}</dd>
                    {r.extension_request && <><dt>Asked for</dt><dd>{r.extension_request}</dd></>}
                    {data.sellers && (
                      <>
                        <dt>Seller</dt>
                        <dd>
                          <select className="ops-inline-select" aria-label="Seller credited with this booking" value={r.seller_id || ""} disabled={busy || !canManage}
                            onChange={(e) => act(() => reservationsAdminAPI.update(r.id, { seller_id: e.target.value || null }), e.target.value ? "Credited to the seller." : "Seller removed.")}>
                            <option value="">None</option>
                            {data.sellers.filter((sl) => sl.active || sl.id === r.seller_id).map((sl) => <option key={sl.id} value={sl.id}>{sl.name}{sl.company ? ` (${sl.company})` : ""}</option>)}
                          </select>
                          {r.seller_id && r.source !== "seller" && <span className="ops-muted"> via their referral link</span>}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              )}

              {section === "rooms" && (
                <div className="ops-card">
                  <div className="ops-card-head">
                    <h3>Accommodation · {r.room_type_name}</h3>
                    {canManage && live && (
                      <button className="ops-btn ops-btn--sm" onClick={() => setModal("move")}>{r.stays.length ? "Move stay" : "Assign room"}</button>
                    )}
                  </div>
                  {r.stays.length ? (
                    <ul className="ops-history">
                      {r.stays.map((s) => (
                        <li key={s.id}><span>{s.room_name}{s.bed_name ? ` · ${s.bed_name}` : ""}</span><span>{range(s.start_date, s.end_date)} · {s.status === "held" ? "held" : "booked"}</span></li>
                      ))}
                    </ul>
                  ) : <p className="ops-note">No room assigned{r.status === "requested" ? ". Assign one, then approve the request." : "."}</p>}
                  <p className="ops-muted" style={{ margin: "0.6rem 0 0" }}>A stay is never split: a move gives the whole stay one unit for every night. Meals: {r.room_meal_service || "—"}.</p>
                </div>
              )}

              {section === "guests" && (
                <div className="ops-card">
                  <div className="ops-table-wrap" style={{ border: 0 }}>
                    <table className="ops-table">
                      <thead><tr><th>Guest</th><th>Food</th><th>Meals</th><th>Cohort</th><th>Screening</th></tr></thead>
                      <tbody>
                        {r.guests.map((g) => (
                          <tr key={g.id}>
                            <td><b>{g.first_name} {g.last_name}</b>{g.is_lead ? <Badge>Lead</Badge> : null}<span className="ops-sub">{[g.email, g.phone, g.country].filter(Boolean).join(" · ")}</span>
                              <span className="ops-sub">{g.room_name ? `${g.room_name}${g.bed_name ? ` · ${g.bed_name}` : ""}` : "No room yet"}</span>
                              {canManage && <button className="ops-btn--link" style={{ fontSize: "0.8rem" }} onClick={() => setModal({ guest: g })}>Edit details</button>}</td>
                            <td>{g.diets.map((d) => d.name).join(", ") || "—"}{g.dietary_notes && <span className="ops-sub">{g.dietary_notes}</span>}</td>
                            <td>
                              <select className="ops-input" aria-label={`Meal service for ${g.first_name}`} value={g.meal_service} disabled={!canManage}
                                onChange={(e) => act(() => reservationsAdminAPI.updateGuest(r.id, g.id, { meal_service: e.target.value }), "Saved.")}>
                                <option value="buffet">Buffet</option><option value="plated">Plated</option>
                              </select>
                            </td>
                            <td>
                              {data.cohorts.length ? (
                                <select className="ops-input" aria-label={`Cohort for ${g.first_name}`} value={g.cohort_id || ""} disabled={!canManage}
                                  onChange={(e) => act(() => reservationsAdminAPI.updateGuest(r.id, g.id, { cohort_id: e.target.value || null }), "Saved.")}>
                                  <option value="">—</option>
                                  {data.cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                              ) : <span className="ops-muted">—</span>}
                            </td>
                            <td>
                              <select className="ops-input" aria-label={`Screening for ${g.first_name}`} value={g.screening_status} disabled={!canManage}
                                onChange={(e) => act(() => reservationsAdminAPI.updateGuest(r.id, g.id, { screening_status: e.target.value }), "Screening updated.")}>
                                {Object.entries(SCREENING).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="ops-muted" style={{ margin: "0.5rem 0 0" }}>Screening shows status only; medical records stay in the clinical system.</p>
                </div>
              )}

              {section === "addons" && (r.addons.length ? (
                <div className="ops-card">
                  <table className="ops-table">
                    <thead><tr><th>Experience</th><th>For</th><th>Status</th><th>Clearance</th><th className="num">Price</th><th /></tr></thead>
                    <tbody>
                      {r.addons.map((a) => {
                        const g = r.guests.find((x) => x.id === a.guest_id);
                        return (
                          <tr key={a.id}>
                            <td>{a.name}<span className="ops-sub">{[a.activity_date && fmtDate(a.activity_date), a.session_time, a.inclusion].filter(Boolean).join(" · ")}</span></td>
                            <td>{g ? g.first_name : "—"}</td>
                            <td><Badge tone={a.status === "confirmed" || a.status === "selected" ? "green" : a.status === "requested" ? "amber" : ""}>{a.status}</Badge></td>
                            <td>{a.clearance === "not_required" ? "—" : <Badge tone={a.clearance === "approved" ? "green" : a.clearance === "declined" ? "red" : "amber"}>{a.clearance}</Badge>}</td>
                            <td className="num">{a.inclusion === "included" ? "Included" : money(a.price)}</td>
                            <td className="num">
                              {canManage && a.clearance === "pending" && <>
                                <button className="ops-btn ops-btn--sm" onClick={() => act(() => reservationsAdminAPI.updateAddon(r.id, a.id, { clearance: "approved", status: "confirmed" }), "Cleared and confirmed.")}>Cleared</button>{" "}
                                <button className="ops-btn ops-btn--sm" onClick={() => act(() => reservationsAdminAPI.updateAddon(r.id, a.id, { clearance: "declined", status: "declined" }), "Recorded.")}>Not cleared</button>
                              </>}
                              {canManage && a.status === "requested" && a.clearance !== "pending" && <>
                                <button className="ops-btn ops-btn--sm" onClick={() => act(() => reservationsAdminAPI.updateAddon(r.id, a.id, { status: "confirmed" }), "Confirmed.")}>Confirm</button>{" "}
                                <button className="ops-btn ops-btn--sm" onClick={() => act(() => reservationsAdminAPI.updateAddon(r.id, a.id, { status: "declined" }), "Declined.")}>Decline</button>
                              </>}
                              {canManage && ["selected", "confirmed"].includes(a.status) && (
                                <button className="ops-btn ops-btn--sm" onClick={() => window.confirm(`Remove ${a.name}?`) && act(() => reservationsAdminAPI.updateAddon(r.id, a.id, { status: "cancelled" }), "Removed.")}>Remove</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <p className="ops-note">No add-ons on this reservation.</p>)}

              {section === "money" && (
                <div className="ops-grid ops-grid--2">
                  <div className="ops-card">
                    <div className="ops-card-head"><h3>Bill</h3>{canPay && <button className="ops-btn ops-btn--sm" onClick={() => setModal("line")}>Add line</button>}</div>
                    <table className="ops-ledger">
                      <tbody>
                        {r.lines.map((l) => (
                          <tr key={l.id}>
                            <td>{l.label}</td>
                            <td className="num">{l.amount === 0 ? "Included" : money(l.amount)}</td>
                            <td className="num" style={{ width: "1.5rem" }}>
                              {canPay && ["adjustment", "fee", "discount"].includes(l.kind) && (
                                <button className="ops-close" style={{ fontSize: "1rem", minHeight: 0 }} aria-label={`Remove ${l.label}`}
                                  onClick={() => window.confirm(`Remove "${l.label}" from the bill?`) && act(() => reservationsAdminAPI.removeLine(r.id, l.id), "Removed.")}>×</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td>Total</td><td className="num">{money(r.total)}</td><td /></tr></tfoot>
                    </table>
                  </div>
                  <div className="ops-card">
                    <div className="ops-card-head"><h3>Payments</h3>{canPay && <button className="ops-btn ops-btn--sm ops-btn--primary" onClick={() => setModal("pay")}>Record payment</button>}</div>
                    {r.payments.length ? (
                      <table className="ops-ledger">
                        <tbody>
                          {r.payments.map((p) => (
                            <tr key={p.id}>
                              <td>{fmtDate(p.paid_at || p.created_at)} · {p.method}{p.status !== "succeeded" ? ` · ${p.status}` : ""}{p.note && <span className="ops-sub">{p.note}</span>}</td>
                              <td className="num">{p.status === "refunded" ? `−${money(p.amount)}` : money(p.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot><tr><td>Paid</td><td className="num">{money(r.paid)}</td></tr><tr><td>Balance</td><td className="num">{money(r.balance)}</td></tr></tfoot>
                      </table>
                    ) : <p className="ops-muted">Nothing paid yet.</p>}
                    {r.installments.length > 0 && (
                      <ul className="ops-history" style={{ marginTop: "0.6rem" }}>
                        {r.installments.map((s) => <li key={s.seq}><span>{fmtDate(s.due_date)}</span><span>{money(s.amount)} · {s.status}</span></li>)}
                      </ul>
                    )}
                    <p className="ops-muted" style={{ margin: "0.6rem 0 0" }}>A bank transfer counts only once it's recorded here; nothing is confirmed on a promise to pay.</p>
                  </div>
                </div>
              )}

              {section === "notes" && <Notes r={r} canEdit={canManage} onDirty={setNotesDirty} onSave={(payload) => act(() => reservationsAdminAPI.update(r.id, payload), "Saved.").then((ok) => { if (ok) setNotesDirty(false); })} />}

              {section === "history" && (
                <div className="ops-card">
                  <ul className="ops-history">
                    {data.history.map((h) => (
                      <li key={h.id}>
                        <time>{fmtStamp(h.created_at)}</time>
                        <span><b>{ACTION_WORDS[h.action] || h.action}</b> · {h.actor}{historyDetail(h)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {modal === "cancel" && r && <CancelModal r={r} onClose={() => setModal(null)} onDone={(reason) => act(() => reservationsAdminAPI.cancel(r.id, reason), "Cancelled.").then((ok) => ok && setModal(null))} />}
      {modal === "pay" && r && <PayModal r={r} onClose={() => setModal(null)} onDone={(p) => act(() => reservationsAdminAPI.pay(r.id, p), "Recorded.").then((ok) => ok && setModal(null))} />}
      {modal === "line" && r && <LineModal onClose={() => setModal(null)} onDone={(p) => act(() => reservationsAdminAPI.addLine(r.id, p), "Added.").then((ok) => ok && setModal(null))} />}
      {modal === "hold" && r && <HoldModal r={r} onClose={() => setModal(null)} onDone={(p) => act(() => reservationsAdminAPI.hold(r.id, p), "Held.").then((ok) => ok && setModal(null))} />}
      {modal === "move" && r && <MoveModal r={r} onClose={() => setModal(null)} onDone={(moves) => act(() => reservationsAdminAPI.reassign(r.id, moves), "Moved.").then((ok) => ok && setModal(null))} />}
      {modal === "dates" && r && <DatesModal r={r} onClose={() => setModal(null)} onDone={(p) => act(() => reservationsAdminAPI.changeDates(r.id, p), "Dates changed.").then((ok) => ok && setModal(null))} />}
      {modal?.guest && r && <GuestModal g={modal.guest} onClose={() => setModal(null)} onDone={(p) => act(() => reservationsAdminAPI.updateGuest(r.id, modal.guest.id, p), "Saved.").then((ok) => ok && setModal(null))} />}
    </>
  );
}

/**
 * New check-in and check-out: previewed first (the nights, the room, the
 * bill as it would be), then saved. Only the nights and the room upgrade
 * are recalculated; the rest of the bill stays as it is.
 */
function DatesModal({ r, onClose, onDone }) {
  const [f, setF] = useState({ arrive_date: r.arrive_date, depart_date: r.depart_date, allow_move: false });
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setPv(null); setErr("");
    if (!f.arrive_date || !f.depart_date || (f.arrive_date === r.arrive_date && f.depart_date === r.depart_date)) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      reservationsAdminAPI.changeDates(r.id, { ...f, dry_run: true })
        .then((res) => { if (alive) setPv(res.data); })
        .catch((e) => { if (alive) setErr(e.message); })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [f.arrive_date, f.depart_date, f.allow_move]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Modal title={`Change the dates of ${r.ref}`} onClose={onClose} wide>
      <p className="ops-muted" style={{ margin: 0 }}>The stay has to cover the retreat ({range(r.retreat_start, r.retreat_end)}); up to 30 extra nights either side. The same room is kept when it's free.</p>
      <div className="ops-grid ops-grid--3">
        <Field label="Check-in"><input type="date" value={f.arrive_date} max={r.retreat_start} onChange={(e) => setF({ ...f, arrive_date: e.target.value })} /></Field>
        <Field label="Check-out"><input type="date" value={f.depart_date} min={r.retreat_end} onChange={(e) => setF({ ...f, depart_date: e.target.value })} /></Field>
        <label className="ops-check" style={{ alignSelf: "end" }}><input type="checkbox" checked={f.allow_move} onChange={(e) => setF({ ...f, allow_move: e.target.checked })} />If the room isn't free, move them to a free one</label>
      </div>
      {loading && <p className="ops-muted">Checking every night…</p>}
      {err && <p className="ops-error">{err}</p>}
      {pv && (
        <div className="ops-card">
          <h3>{pv.nights} nights, {fmtDate(pv.arrive)} to {fmtDate(pv.depart)}{pv.moved ? " · in a different room" : " · same room"}</h3>
          <p className="ops-muted" style={{ margin: "0 0 0.5rem" }}>{pv.units.join(", ") || "No room (a request)"}</p>
          <table className="ops-ledger">
            <tbody>{pv.lines.map((l, i) => <tr key={i}><td>{l.label}</td><td className="num">{money(l.amount)}</td></tr>)}</tbody>
            <tfoot><tr><td>New total (was {money(pv.was)})</td><td className="num">{money(pv.total)}</td></tr></tfoot>
          </table>
          {pv.warnings.filter((w) => !w.startsWith("The bill went")).map((w) => <p key={w} className="ops-error" style={{ marginTop: "0.5rem" }}>{w}</p>)}
        </div>
      )}
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={!pv} onClick={() => onDone({ arrive_date: f.arrive_date, depart_date: f.depart_date, allow_move: f.allow_move })}>Save new dates</button></div>
    </Modal>
  );
}

function GuestModal({ g, onClose, onDone }) {
  const [f, setF] = useState({ first_name: g.first_name || "", last_name: g.last_name || "", email: g.email || "", phone: g.phone || "", country: g.country || "", dietary_notes: g.dietary_notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={`${g.first_name} ${g.last_name}`} onClose={onClose}>
      <div className="ops-grid ops-grid--2">
        <Field label="First name"><input value={f.first_name} onChange={set("first_name")} /></Field>
        <Field label="Last name"><input value={f.last_name} onChange={set("last_name")} /></Field>
        <Field label="Email"><input type="email" value={f.email} onChange={set("email")} /></Field>
        <Field label="Phone"><input value={f.phone} onChange={set("phone")} /></Field>
        <Field label="Country"><input value={f.country} onChange={set("country")} /></Field>
        <Field label="Food notes for the kitchen" className="ops-span-all"><input value={f.dietary_notes} onChange={set("dietary_notes")} /></Field>
      </div>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Cancel</button><button className="ops-btn ops-btn--primary" disabled={!f.first_name.trim() || !f.last_name.trim()} onClick={() => onDone(f)}>Save</button></div>
    </Modal>
  );
}

const historyDetail = (h) => {
  const d = h.detail;
  if (!d || typeof d !== "object") return "";
  if (d.amount != null && h.action.includes("payment")) return ` · ${money(d.amount)}`;
  if (d.reason) return ` · ${d.reason}`;
  if (d.guest) return ` · ${d.guest}${d.screening_status ? ` → ${SCREENING[d.screening_status]?.label}` : ""}`;
  if (d.addon) return ` · ${d.addon} → ${d.status}`;
  if (d.label) return ` · ${d.label}`;
  return "";
};

function Notes({ r, onSave, canEdit, onDirty }) {
  const [f, setF] = useState({
    admin_notes: r.admin_notes || "", special_requests: r.special_requests || "",
    accessibility: r.accessibility || "", room_preferences: r.room_preferences || "",
  });
  const dirty = Object.keys(f).some((k) => (r[k] || "") !== f[k]);
  useEffect(() => { onDirty?.(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="ops-card">
      <div className="ops-card-head"><h3>Requests and notes</h3>
        {canEdit && <div className="ops-actions">{dirty && <span className="ops-dirty">Unsaved changes</span>}<button className="ops-btn ops-btn--sm ops-btn--primary" disabled={!dirty} onClick={() => onSave(f)}>Save notes</button></div>}</div>
      <div className="ops-grid ops-grid--2">
        <Field label="Accessibility"><textarea value={f.accessibility} onChange={(e) => setF({ ...f, accessibility: e.target.value })} /></Field>
        <Field label="Room preferences"><textarea value={f.room_preferences} onChange={(e) => setF({ ...f, room_preferences: e.target.value })} /></Field>
        <Field label="From the guest"><textarea value={f.special_requests} onChange={(e) => setF({ ...f, special_requests: e.target.value })} /></Field>
        <Field label="Staff notes (never shown to the guest)"><textarea value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} /></Field>
      </div>
      {r.heard_from && <p className="ops-muted" style={{ margin: "0.5rem 0 0" }}>Heard about us: {r.heard_from}</p>}
    </div>
  );
}

function CancelModal({ r, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Cancel ${r.ref}`} onClose={onClose}>
      <p className="ops-muted">The rooms, places and add-on places go back on sale straight away. Refunds are recorded separately, under Payments.</p>
      <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Guest asked to cancel" /></Field>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Keep it</button><button className="ops-btn ops-btn--danger" onClick={() => onDone(reason)}>Cancel reservation</button></div>
    </Modal>
  );
}

function PayModal({ r, onClose, onDone }) {
  const due = Math.max(0, r.status === "confirmed" ? r.balance : r.due_now - r.paid);
  const [p, setP] = useState({ amount: due || "", method: r.payment_method || "bank", paid_at: new Date().toISOString().slice(0, 10), note: "", refund: false });
  return (
    <Modal title="Record a payment" onClose={onClose}>
      <p className="ops-muted">For money received outside a card processor: bank transfer, cash, crypto. Paying what's due now confirms a held reservation.</p>
      <div className="ops-grid ops-grid--2">
        <Field label="Amount (USD)"><input type="number" min="0" step="0.01" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} /></Field>
        <Field label="Method">
          <select value={p.method} onChange={(e) => setP({ ...p, method: e.target.value })}>
            {["bank", "card", "crypto", "cash", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Date received"><input type="date" value={p.paid_at} onChange={(e) => setP({ ...p, paid_at: e.target.value })} /></Field>
        <Field label="Note"><input value={p.note} onChange={(e) => setP({ ...p, note: e.target.value })} placeholder="Transfer ref, who took it…" /></Field>
      </div>
      <label className="ops-check"><input type="checkbox" checked={p.refund} onChange={(e) => setP({ ...p, refund: e.target.checked })} />This is a refund paid back to the guest</label>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button><button className="ops-btn ops-btn--primary" disabled={!(Number(p.amount) > 0)} onClick={() => onDone(p)}>{p.refund ? "Record refund" : "Record payment"}</button></div>
    </Modal>
  );
}

function LineModal({ onClose, onDone }) {
  const [p, setP] = useState({ label: "", amount: "", kind: "adjustment" });
  return (
    <Modal title="Add to the bill" onClose={onClose}>
      <div className="ops-grid ops-grid--2">
        <Field label="Description" className="ops-span-2"><input value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} placeholder="Airport transfer, loyalty discount…" /></Field>
        <Field label="Amount (negative for a discount)"><input type="number" step="0.01" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} /></Field>
        <Field label="Kind">
          <select value={p.kind} onChange={(e) => setP({ ...p, kind: e.target.value })}>
            <option value="adjustment">Adjustment</option><option value="fee">Fee</option><option value="discount">Discount</option>
          </select>
        </Field>
      </div>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button><button className="ops-btn ops-btn--primary" disabled={!p.label.trim() || !Number(p.amount)} onClick={() => onDone(p)}>Add</button></div>
    </Modal>
  );
}

function HoldModal({ r, onClose, onDone }) {
  const inThree = new Date(Date.now() + 72 * 3600000);
  const [p, setP] = useState({ until: `${inThree.toISOString().slice(0, 10)}T12:00`, due_now: r.status === "requested" ? r.total : r.due_now, notify: true });
  return (
    <Modal title={r.status === "requested" ? "Approve the request" : "Hold the room again"} onClose={onClose}>
      <p className="ops-muted">
        {r.status === "requested"
          ? "Assign a room first (Room → Assign room) and add any custom pricing to the bill. The guest is then asked to pay what's due now."
          : "Everything is checked again: if the room or a place has gone since, you'll be told and nothing changes."}
      </p>
      <div className="ops-grid ops-grid--2">
        <Field label="Hold until"><input type="datetime-local" value={p.until} onChange={(e) => setP({ ...p, until: e.target.value })} /></Field>
        <Field label="Due now (USD)"><input type="number" min="0" step="0.01" value={p.due_now} onChange={(e) => setP({ ...p, due_now: e.target.value })} /></Field>
      </div>
      <label className="ops-check"><input type="checkbox" checked={p.notify} onChange={(e) => setP({ ...p, notify: e.target.checked })} />Email the guest how to pay</label>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button><button className="ops-btn ops-btn--primary" onClick={() => onDone(p)}>Hold</button></div>
    </Modal>
  );
}

/** Choose new units for each stay; the server checks every night is free. */
function MoveModal({ r, onClose, onDone }) {
  const { data, loading, error } = useLoad(() => inventoryAPI.free(r.arrive_date, r.depart_date, r.id), [r.id]);
  // Nothing assigned yet (a request): one pick per bed for a dorm, or per
  // room for a private room — whoever isn't placed yet goes in the room.
  const targets = r.stays.length ? r.stays
    : r.sell_by === "bed" ? r.guests.map((g) => ({ id: null, guest_id: g.id }))
    : Array.from({ length: Math.max(1, r.units_count) }, () => ({ id: null }));
  const [picks, setPicks] = useState({});
  const units = (data?.categories || []).flatMap((c) => c.units.map((u) => ({ ...u, category: c.name })));
  const ownUnits = r.stays.map((s) => ({ room_id: s.room_id, bed_id: s.bed_id, label: `${s.room_name}${s.bed_name ? ` · ${s.bed_name}` : ""} (current)`, category: "Current" }));
  const options = [...ownUnits, ...units];
  const keyOf = (u) => `${u.room_id}:${u.bed_id || ""}`;
  const moves = targets.map((t, i) => {
    const k = picks[i];
    if (!k) return null;
    const u = options.find((o) => keyOf(o) === k);
    return u ? { stay_id: t.id || undefined, guest_id: t.guest_id || undefined, room_id: u.room_id, bed_id: u.bed_id } : null;
  }).filter(Boolean);

  return (
    <Modal title={r.stays.length ? "Move the whole stay" : "Assign a room"} onClose={onClose} wide>
      <p className="ops-muted">{range(r.arrive_date, r.depart_date)}. Only units free for every one of these nights are listed, so a stay is never split.</p>
      {loading && <p className="ops-muted">Finding free rooms…</p>}
      {error && <p className="ops-error">{error}</p>}
      {data && targets.map((t, i) => (
        <Field key={i} label={t.id ? `Now: ${t.room_name}${t.bed_name ? ` · ${t.bed_name}` : ""}` : t.guest_id ? `${r.guests.find((g) => g.id === t.guest_id)?.first_name || "Guest"}` : `Room ${i + 1}`}>
          <select value={picks[i] || ""} onChange={(e) => setPicks({ ...picks, [i]: e.target.value })}>
            <option value="">Choose…</option>
            {[...new Set(options.map((o) => o.category))].map((cat) => (
              <optgroup key={cat} label={cat}>
                {options.filter((o) => o.category === cat).map((o) => <option key={keyOf(o)} value={keyOf(o)}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
      ))}
      {moves.some((m) => { const u = options.find((o) => `${o.room_id}:${o.bed_id || ""}` === `${m.room_id}:${m.bed_id || ""}`); return u && u.category !== "Current" && u.category !== r.room_type_name; })
        ? <p className="ops-error">This moves them out of {r.room_type_name}. The bill still charges {r.room_type_name}'s upgrade: add a line if they should pay more or less.</p>
        : <p className="ops-note">The bill isn't changed by a move within the same room type.</p>}
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button>
        <button className="ops-btn ops-btn--primary" disabled={!moves.length} onClick={() => onDone(moves)}>{r.stays.length ? "Move" : "Assign"}</button></div>
    </Modal>
  );
}
